import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Wallet, ArrowUpCircle, ArrowDownCircle, Loader, RefreshCw } from 'lucide-react';
import apiService from '../../apiService';

const SOURCE_LABELS = {
  topup: 'Wallet Top-up',
  refund: 'Order Refund',
  reward: 'Reward',
  cashback: 'Cashback',
  order_payment: 'Order Payment',
  admin_credit: 'Admin Credit',
  admin_debit: 'Admin Debit',
};

const WalletTransactionsModal = ({ isOpen, onClose, walletBalance }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState('');

  const loadTransactions = useCallback(async (pageNum = 1, append = false) => {
    setLoading(true);
    setError('');
    try {
      const resp = await apiService.getWalletTransactions(pageNum);
      if (resp?.success) {
        const data = resp.data?.transactions ?? resp.data ?? {};
        const items = data.data ?? [];
        setTransactions(prev => (append ? [...prev, ...items] : items));
        setHasMore((data.current_page ?? pageNum) < (data.last_page ?? 1));
        setPage(data.current_page ?? pageNum);
      } else {
        setError(resp?.data?.message || 'Failed to load transactions');
      }
    } catch {
      setError('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setTransactions([]);
      setPage(1);
      loadTransactions(1, false);
    }
  }, [isOpen, loadTransactions]);

  const currentUser = apiService.getUser();
  const isAdmin = currentUser && (currentUser.is_admin || currentUser.role === 'admin');

  const handleTransactionAction = async (txnId, action) => {
    if (!isAdmin) return;
    try {
      setLoading(true);
      const resp = await apiService.adminTransactionAction(txnId, action);
      if (resp?.success) {
        loadTransactions(1, false);
      } else {
        alert(resp?.data?.message || 'Action failed');
      }
    } catch (e) {
      console.error(e);
      alert('Action failed');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const totalCredits = transactions
    .filter(t => t.type === 'credit')
    .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);

  const totalDebits = transactions
    .filter(t => t.type === 'debit')
    .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);

  return (
    <div className="fixed inset-0 z-[300] bg-gray-50 flex justify-center overflow-y-auto">
      <div className="w-full sm:max-w-3xl bg-white shadow-xl rounded-3xl overflow-hidden m-4">
        <div className="bg-white border-b border-gray-100 px-4 pt-5 pb-4">
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft size={22} className="text-gray-700" />
            </button>
            <h1 className="text-lg font-bold text-gray-800">Wallet Transactions</h1>
            <button
              onClick={() => loadTransactions(1, false)}
              disabled={loading}
              className="ml-auto p-2 rounded-full hover:bg-gray-100 transition-colors disabled:opacity-40"
            >
              <RefreshCw size={18} className={`text-gray-500 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-orange-50 rounded-xl p-3 border border-orange-100">
              <p className="text-xs text-orange-500 font-medium mb-1">Available Balance</p>
              <p className="text-xl font-bold text-orange-700">
                ₹{typeof walletBalance === 'number' ? walletBalance.toFixed(2) : '0.00'}
              </p>
            </div>
            <div className="bg-green-50 rounded-xl p-3 border border-green-100">
              <p className="text-xs text-green-600 font-medium mb-1">Credits</p>
              <p className="text-lg font-bold text-green-700">+₹{totalCredits.toFixed(2)}</p>
            </div>
            <div className="bg-red-50 rounded-xl p-3 border border-red-100">
              <p className="text-xs text-red-500 font-medium mb-1">Debits</p>
              <p className="text-lg font-bold text-red-600">-₹{totalDebits.toFixed(2)}</p>
            </div>
          </div>
        </div>

        <div className="p-4">
          {loading && transactions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-14 text-gray-500">
              <Loader size={32} className="animate-spin text-orange-500" />
              <p className="mt-3">Loading transactions...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-14 text-center text-gray-600">
              <Wallet size={44} className="text-gray-300" />
              <p className="mt-3 text-red-500">{error}</p>
              <button
                onClick={() => loadTransactions(1, false)}
                className="mt-3 text-orange-600 text-sm font-medium underline"
              >
                Try again
              </button>
            </div>
          ) : transactions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-14 text-center text-gray-500">
              <div className="w-16 h-16 rounded-full bg-orange-50 flex items-center justify-center">
                <Wallet size={32} className="text-orange-300" />
              </div>
              <p className="mt-3 font-semibold text-gray-700">No transactions yet</p>
              <p className="text-sm text-gray-400">Top up your wallet to get started.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {transactions.map(txn => {
                const isCredit = txn.type === 'credit';
                const label = SOURCE_LABELS[txn.source] ?? txn.source_label ?? txn.source ?? 'Transaction';
                return (
                  <div
                    key={txn.id}
                    className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm flex items-center gap-3"
                  >
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isCredit ? 'bg-green-100' : 'bg-red-100'}`}>
                      {isCredit ? (
                        <ArrowUpCircle size={22} className="text-green-600" />
                      ) : (
                        <ArrowDownCircle size={22} className="text-red-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">{label}</p>
                      {txn.description && (
                        <p className="text-xs text-gray-500 truncate mt-1">{txn.description}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">{formatDate(txn.created_at)}</p>
                      {txn.status === 'pending_verification' && (
                        <p className="text-xs text-yellow-600 mt-1">Pending verification</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className={`font-bold ${isCredit ? 'text-green-600' : 'text-red-500'}`}>
                        {isCredit ? '+' : '-'}₹{parseFloat(txn.amount || 0).toFixed(2)}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">Bal: ₹{parseFloat(txn.balance_after || 0).toFixed(2)}</p>
                    </div>
                    {isAdmin && txn.status === 'pending_verification' && (
                      <div className="flex flex-col gap-2">
                        <button
                          onClick={() => handleTransactionAction(txn.id, 'approve')}
                          disabled={loading}
                          className="rounded-xl bg-green-50 px-3 py-1 text-sm text-green-700 border border-green-100 hover:bg-green-100 disabled:opacity-50"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleTransactionAction(txn.id, 'reject')}
                          disabled={loading}
                          className="rounded-xl bg-red-50 px-3 py-1 text-sm text-red-700 border border-red-100 hover:bg-red-100 disabled:opacity-50"
                        >
                          Reject
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}

              {hasMore && (
                <button
                  onClick={() => loadTransactions(page + 1, true)}
                  disabled={loading}
                  className="w-full rounded-2xl border border-orange-200 bg-white py-3 text-sm font-semibold text-orange-600 hover:bg-orange-50 disabled:opacity-50"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader size={16} className="animate-spin" /> Loading...
                    </span>
                  ) : (
                    'Load More'
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WalletTransactionsModal;
